import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HiMenu, HiX } from 'react-icons/hi';
import { useApp } from '../../context/AppContext';
import siteImages from '../../config/images';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const { settings } = useApp();

  // Check if we're on a property detail page that needs white header
  const isPropertyPage = location.pathname.includes('/byt/');
  const needsWhiteHeader = isPropertyPage;

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Determine if scrolling up or down
      if (currentScrollY > 100) { // Only checking after some scroll
        if (currentScrollY > lastScrollY) {
          // Scrolling DOWN
          setIsVisible(false);
        } else {
          // Scrolling UP
          setIsVisible(true);
        }
      } else {
        // At the top
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
      setIsScrolled(currentScrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Domů', path: '/' },
    {
      name: 'Koupit byt',
      path: '/koupit-byt',
      dropdown: [
        { name: 'K nastěhování', path: '/koupit-byt' },
        { name: 'Připravujeme', path: '/pripravujeme' },
        { name: 'Realizováno', path: '/realizace' },
      ]
    },
    { name: 'Prodat byt', path: '/prodat-byt' },
    { name: 'Spolupráce', path: '/spoluprace' },
    { name: 'Rychlý výkup', path: '/rychly-vykup' },
    { name: 'Kontakt', path: '/kontakt' },
  ];

  const headerClass = `fixed top-0 left-0 right-0 z-50 transition-all duration-300 transform ${isVisible ? 'translate-y-0' : '-translate-y-full'
    } ${needsWhiteHeader
      ? 'bg-white shadow-lg backdrop-blur-md'
      : isScrolled
        ? 'bg-white shadow-lg backdrop-blur-md'
        : 'bg-transparent'
    }`;



  return (
    <>
      <header className={headerClass}>
        <div className="container-custom">
          <nav className="flex items-center justify-between py-4">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 group">
              <img 
                src="/logo.svg" 
                alt="PRO-BYTY.cz" 
                className={`h-10 w-auto transition-all duration-300 ${!isScrolled && !needsWhiteHeader ? 'brightness-0 invert' : ''}`} 
              />
            </Link>

            {/* Desktop Navigation */}
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-6">
              {navLinks.map((link) => (
                <div
                  key={link.path}
                  className="relative group"
                  onMouseEnter={() => link.dropdown && setActiveDropdown(link.name)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  {link.dropdown ? (
                    // Link with dropdown
                    <div className="relative py-4">
                      <Link
                        to={link.path}
                        className={`relative font-medium transition-all duration-300 flex items-center ${location.pathname === link.path || link.dropdown.some(item => location.pathname === item.path)
                          ? isScrolled || needsWhiteHeader
                            ? 'text-navy'
                            : 'text-white'
                          : isScrolled || needsWhiteHeader
                            ? 'text-gray-600 hover:text-navy'
                            : 'text-white/80 hover:text-white'
                          }`}
                      >
                        {link.name}
                        <svg className={`ml-1 w-4 h-4 transition-transform duration-200 ${activeDropdown === link.name ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </Link>

                      {/* Dropdown Menu */}
                      <AnimatePresence>
                        {activeDropdown === link.name && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.2 }}
                            className="absolute top-full left-0 w-48 bg-white rounded-lg shadow-xl py-2 z-50 mt-1 overflow-hidden"
                          >
                            {link.dropdown.map((item, idx) => (
                              <Link
                                key={item.path}
                                to={item.path}
                                className={`block px-4 py-3 text-gray-700 hover:bg-beige hover:text-navy transition-colors border-b last:border-0 border-gray-100 ${location.pathname === item.path ? 'bg-beige text-navy font-semibold' : ''
                                  }`}
                              >
                                {item.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    // Regular link without dropdown
                    <Link
                      to={link.path}
                      className={`relative font-medium transition-all duration-300 ${location.pathname === link.path
                        ? isScrolled || needsWhiteHeader
                          ? 'text-navy'
                          : 'text-white'
                        : isScrolled || needsWhiteHeader
                          ? 'text-gray-600 hover:text-navy'
                          : 'text-white/80 hover:text-white'
                        }`}
                    >
                      {link.name}
                      {location.pathname === link.path && (
                        <motion.div
                          layoutId="activeTab"
                          className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-gold to-navy"
                          initial={false}
                          transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        />
                      )}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* CTA Button - Desktop */}
            <div className="hidden lg:block">
              <Link
                to="/koupit-byt"
                className={`px-6 py-2.5 rounded-full font-semibold transition-all transform hover:scale-105 hover:shadow-lg ${isScrolled || needsWhiteHeader
                  ? 'bg-gradient-to-r from-gold to-gold-dark text-white'
                  : 'bg-white/20 backdrop-blur-sm text-white border border-white/30 hover:bg-white/30'
                  }`}
              >
                Najít domov
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 rounded-lg transition-colors ${isScrolled || needsWhiteHeader ? 'text-navy hover:bg-navy/10' : 'text-white hover:bg-white/20'
                }`}
              aria-label={isMobileMenuOpen ? "Zavřít menu" : "Otevřít navigační menu"}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <HiX size={28} /> : <HiMenu size={28} />}
            </button>
          </nav>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/50 lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Menu Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 h-screen w-full sm:w-96 z-50 lg:hidden overflow-hidden"
            >
              {/* Background Image with Overlay */}
              <div className="absolute inset-0">
                <img
                  src={siteImages.propertyPlaceholder}
                  alt="Background"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-navy/95 via-navy/90 to-navy/95" />
              </div>

              {/* Content */}
              <div className="relative h-full flex flex-col">
                {/* Header - Smaller */}
                <div className="flex items-center justify-between p-4">
                  <Link to="/" className="flex items-center space-x-2" onClick={() => setIsMobileMenuOpen(false)}>
                    <img src="/logo.svg" alt="PRO-BYTY.cz" className="h-8 w-auto brightness-0 invert" />
                  </Link>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 rounded-lg bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-colors"
                    aria-label="Zavřít navigační menu"
                  >
                    <HiX size={24} />
                  </button>
                </div>

                {/* Navigation Links */}
                <nav className="flex-1 px-4 py-2 overflow-hidden">
                  <div className="space-y-1">
                    {navLinks.map((link, index) => (
                      <motion.div
                        key={link.path}
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        {link.dropdown ? (
                          <div className="mb-2">
                            <div className="px-3 py-2 text-white/50 text-xs font-bold uppercase tracking-wider mb-1">
                              {link.name}
                            </div>
                            {link.dropdown.map((subLink) => (
                              <Link
                                key={subLink.path}
                                to={subLink.path}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`group flex items-center py-2 px-3 pl-6 rounded-lg text-base font-medium transition-all ${location.pathname === subLink.path
                                  ? 'bg-white/10 text-gold border-l-2 border-gold'
                                  : 'text-white/80 hover:text-white hover:bg-white/5'
                                  }`}
                              >
                                <span className="flex-1">{subLink.name}</span>
                              </Link>
                            ))}
                          </div>
                        ) : (
                          <Link
                            to={link.path}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`group flex items-center py-2.5 px-3 rounded-lg text-base font-medium transition-all ${location.pathname === link.path
                              ? 'bg-white text-navy shadow-lg'
                              : 'text-white hover:bg-white/10 backdrop-blur-sm'
                              }`}
                          >
                            <span className="flex-1">{link.name}</span>
                            {location.pathname === link.path && (
                              <motion.div
                                layoutId="activeMobileTab"
                                className="w-1.5 h-1.5 rounded-full bg-navy mr-2"
                              />
                            )}
                          </Link>
                        )}
                      </motion.div>
                    ))}

                  </div>
                </nav>

                {/* Bottom Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="p-4 bg-gradient-to-t from-navy to-transparent"
                >
                  <Link
                    to="/koupit-byt"
                    className="block w-full py-2.5 px-4 bg-gradient-to-r from-gold to-gold-dark text-white text-center font-semibold rounded-lg shadow-xl transition-all"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Najít domov
                  </Link>

                  <div className="mt-3 text-center">
                    <p className="text-white/90 font-semibold text-sm mb-1">Kontaktujte nás</p>
                    <a
                      href={`tel:${settings.contact_phone?.replace(/\s/g, '')}`}
                      className="block text-white/70 hover:text-white transition-colors text-sm"
                      aria-label={`Zavolat na ${settings.contact_phone || '+420 777 123 456'}`}
                    >
                      {settings.contact_phone || '+420 777 123 456'}
                    </a>
                    <a
                      href={`mailto:${settings.contact_email}`}
                      className="block text-white/70 hover:text-white transition-colors text-sm mt-0.5"
                      aria-label={`Napsat email na ${settings.contact_email || 'info@pro-byty.cz'}`}
                    >
                      {settings.contact_email || 'info@pro-byty.cz'}
                    </a>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </>
        )
        }
      </AnimatePresence >
    </>
  );
};

export default Header;